import { useState, useRef, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { getItem, setItem } from '../utils/idb';

export type TerminalPhase = 'boot' | 'command' | 'password';
export type HistoryItem = { type: 'cmd' | 'res' | 'err' | 'success' | 'dim'; text: string; onClick?: () => void };

export type TerminalContext = {
  print: (text: string, type?: HistoryItem['type']) => void;
  printLines: (lines: { text: string; type?: HistoryItem['type']; onClick?: () => void }[]) => void;
  clear: () => void;
  setPhase: (phase: TerminalPhase) => void;
  setUsername: (username: string) => void;
  username: string;
  requestPassword: () => Promise<string>;
};

export type TerminalCommand = {
  description: string;
  handler: (args: string[], context: TerminalContext) => void | Promise<void>;
  completions?: (args: string[]) => string[];
};

export type UseTerminalProps = {
  bootLines?: string[];
  bootSpeed?: number;
  initialHistory?: HistoryItem[];
  commands?: Record<string, TerminalCommand>;
  onCommandNotFound?: (cmd: string, context: TerminalContext) => void;
};

export function useTerminal({ 
  bootLines = [], 
  bootSpeed = 50,
  initialHistory = [],
  commands = {}, 
  onCommandNotFound 
}: UseTerminalProps = {}) {
  const [phase, setPhase] = useState<TerminalPhase>(bootLines.length > 0 ? 'boot' : 'command');
  const [history, setHistory] = useState<HistoryItem[]>(initialHistory);
  const [input, setInput] = useState('');
  const [username, setUsername] = useState('admin');
  const [lineIndex, setLineIndex] = useState(0);
  
  // Command history for up/down navigation
  const [cmdHistory, setCmdHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [draftInput, setDraftInput] = useState('');
  
  const passwordResolverRef = useRef<((pwd: string) => void) | undefined>(undefined);

  const context: TerminalContext = {
    print: (text, type = 'res') => setHistory(p => [...p, { text, type }]),
    printLines: (lines) => setHistory(p => [...p, ...lines.map(l => ({ text: l.text, type: l.type || 'res', onClick: l.onClick }))]),
    clear: () => setHistory([]),
    setPhase,
    setUsername,
    username,
    requestPassword: () => {
      setPhase('password');
      return new Promise<string>(resolve => {
        passwordResolverRef.current = resolve;
      });
    }
  };

  // Load history from IndexedDB on mount
  useEffect(() => {
    getItem<string[]>('terminal_history').then(hist => {
      if (hist && Array.isArray(hist)) {
        setCmdHistory(hist);
      }
    });
  }, []);

  // Boot sequence logic
  useEffect(() => {
    if (phase !== 'boot') return;
    if (lineIndex >= bootLines.length) {
      setPhase('command');
      return;
    }
    const timeout = setTimeout(() => {
      setLineIndex((prev) => prev + 1);
    }, bootSpeed);
    return () => clearTimeout(timeout);
  }, [lineIndex, phase, bootLines.length, bootSpeed]);

  const handleExecute = async (cmdStr: string) => {
    const trimmed = cmdStr.trim();
    if (!trimmed) return;
    context.print(trimmed, 'cmd');
    
    // Save to history if it's not a password
    if (phase === 'command') {
       setCmdHistory(prev => {
         const newHist = [...prev, trimmed];
         // Keep last 50
         const finalHist = newHist.length > 50 ? newHist.slice(newHist.length - 50) : newHist;
         setItem('terminal_history', finalHist);
         return finalHist;
       });
    }
    setHistoryIndex(-1);
    setDraftInput('');
    
    const parts = trimmed.split(/\s+/);
    const cmdName = parts[0].toLowerCase();
    const args = parts.slice(1);

    if (commands[cmdName]) {
      try {
        await commands[cmdName].handler(args, context);
      } catch (e: any) {
        context.print(e.message || 'Execution error', 'err');
      }
    } else if (onCommandNotFound) {
      onCommandNotFound(cmdName, context);
    } else {
      context.print(`UNKNOWN COMMAND: ${cmdName}`, 'err');
    }
  };

  const handleTab = (currentInput: string) => {
    const parts = currentInput.split(' ');
    if (parts.length === 1) {
      const cmdName = parts[0].toLowerCase();
      const matches = Object.keys(commands).filter(c => c.startsWith(cmdName));
      if (matches.length === 1) {
        setInput(matches[0] + ' ');
      } else if (matches.length > 1) {
        context.print(currentInput, 'cmd');
        context.print(matches.join('  '), 'res');
      }
    } else {
      const cmdName = parts[0].toLowerCase();
      if (commands[cmdName] && commands[cmdName].completions) {
        const args = parts.slice(1);
        const matches = commands[cmdName].completions!(args);
        if (matches.length === 1) {
          setInput(cmdName + ' ' + args.slice(0, -1).join(' ') + (args.length > 1 ? ' ' : '') + matches[0] + ' ');
        } else if (matches.length > 1) {
          context.print(currentInput, 'cmd');
          context.print(matches.join('  '), 'res');
        }
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = input.trim();
      if (phase === 'command') {
        setInput('');
        handleExecute(val);
      } else if (phase === 'password') {
        context.print('*'.repeat(val.length), 'cmd');
        setInput('');
        if (passwordResolverRef.current) {
          passwordResolverRef.current(val);
          passwordResolverRef.current = undefined;
        }
      }
    } else if (e.key === 'Tab' && phase === 'command') {
      e.preventDefault();
      handleTab(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (phase === 'command' && cmdHistory.length > 0) {
        if (historyIndex === -1) setDraftInput(input);
        const nextIndex = historyIndex === -1 ? cmdHistory.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(nextIndex);
        setInput(cmdHistory[nextIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (phase === 'command' && historyIndex !== -1) {
        const nextIndex = historyIndex + 1;
        if (nextIndex >= cmdHistory.length) {
          setHistoryIndex(-1);
          setInput(draftInput);
        } else {
          setHistoryIndex(nextIndex);
          setInput(cmdHistory[nextIndex]);
        }
      }
    }
  };

  return {
    phase,
    history,
    input,
    setInput,
    username,
    lineIndex,
    bootLines,
    handleKeyDown,
    context
  };
}
